import { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { track, identify } from "@/lib/tracking";
import { refreshIsAdmin, clearIsAdminCache } from "@/lib/isAdminUser";

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** Sign up a new user with email + password. */
  signUpWithPassword: (
    email: string,
    password: string,
    profile?: { firstName?: string; lastName?: string },
  ) => Promise<{ error: Error | null }>;
  /** Sign in an existing user with email + password. */
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ error: Error | null }>;
  /** Send a password reset email. */
  sendPasswordReset: (email: string) => Promise<{ error: Error | null }>;
  /** Resend the email verification link to a user who signed up but hasn't confirmed yet. */
  resendVerificationEmail: (email: string) => Promise<{ error: Error | null }>;
  /** Update the current user's password (used after recovery). */
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  /** Legacy: sends a 6-digit OTP code (used by /verify assessment flow). */
  sendEmailOtp: (email: string) => Promise<{ error: Error | null }>;
  /** Legacy: verifies a 6-digit OTP code (used by /verify assessment flow). */
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const identifiedRef = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setLoading(false);
      if (newSession?.user && !identifiedRef.current) {
        identifiedRef.current = true;
        identify();
      }
      if (newSession?.user) {
        void refreshIsAdmin();
      }
      if (event === "SIGNED_OUT") {
        identifiedRef.current = false;
        clearIsAdminCache();
      }
    });

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      setLoading(false);
      if (existing?.user && !identifiedRef.current) {
        identifiedRef.current = true;
        identify();
      }
      if (existing?.user) {
        void refreshIsAdmin();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUpWithPassword = async (
    email: string,
    password: string,
    profile?: { firstName?: string; lastName?: string },
  ) => {
    track("signup_started", { email });
    const firstName = profile?.firstName?.trim() || undefined;
    const lastName = profile?.lastName?.trim() || undefined;
    const displayName =
      firstName || lastName ? [firstName, lastName].filter(Boolean).join(" ") : undefined;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/results`,
        data: {
          ...(firstName ? { first_name: firstName } : {}),
          ...(lastName ? { last_name: lastName } : {}),
          ...(displayName ? { display_name: displayName } : {}),
        },
      },
    });
    if (error) {
      track("signup_failed", { email, success: false, metadata: { reason: error.message } });
      return { error };
    }
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      track("signup_failed", { email, success: false, metadata: { reason: "email_exists" } });
      return {
        error: new Error(
          "An account with this email already exists. Try signing in, or use 'Forgot password' to set a new one."
        ),
      };
    }
    track("signup_completed", { email });
    return { error: null };
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      track("login_failed", { email, success: false, metadata: { reason: error.message } });
    } else {
      track("login_success", { email });
    }
    return { error };
  };

  const sendPasswordReset = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    track("password_reset_requested", { email, success: !error });
    return { error };
  };

  const resendVerificationEmail = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/results` },
    });
    track("email_verification_sent", { email, success: !error });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    track("password_changed", { success: !error });
    return { error };
  };

  const sendEmailOtp = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    });
    return { error };
  };

  const verifyEmailOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: "email" });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUpWithPassword,
        signInWithPassword,
        sendPasswordReset,
        resendVerificationEmail,
        updatePassword,
        sendEmailOtp,
        verifyEmailOtp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
